import React, { useState, useEffect } from "react";
import Modal from "newui/components/ui/Modal/Modal";
import type { DeclineReason } from "../../../../types/clarification-review.type";
import { DECLINE_REASON_LABELS } from "../../../../types/clarification-review.type";

interface DeclineReasonModalProps {
	isOpen: boolean;
	count: number;
	onClose: () => void;
	onConfirm: (reason: string, category: DeclineReason) => void;
	isLoading: boolean;
}

export const DeclineReasonModal: React.FC<DeclineReasonModalProps> = ({
	isOpen,
	count,
	onClose,
	onConfirm,
	isLoading,
}) => {
	const [category, setCategory] = useState<DeclineReason>("not_relevant");
	const [otherReason, setOtherReason] = useState("");

	useEffect(() => {
		if (isOpen) {
			setCategory("not_relevant");
			setOtherReason("");
		}
	}, [isOpen]);

	const isValid = category !== "other" || otherReason.trim().length > 0;

	const handleConfirm = () => {
		if (!isValid) {
			return;
		}
		const reason = category === "other"
			? otherReason.trim()
			: DECLINE_REASON_LABELS[category];
		onConfirm(reason, category);
	};

	const label = count === 1 ? "clarification" : "clarifications";

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={`Decline ${count} ${label}`}
			size="md"
			modalType="standard"
			iconType="warning"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: isLoading ? "Declining..." : `Decline ${count} ${label}`,
				onClick: handleConfirm,
				btntype: "primary",
				disabled: isLoading || !isValid,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onClose,
			}}
		>
			<div className="space-y-3">
				<p className="text-sm text-gray-600">
					Select a reason for declining {count > 1 ? `these ${count} clarifications` : "this clarification"}:
				</p>
				<div className="space-y-2">
					{(Object.keys(DECLINE_REASON_LABELS) as DeclineReason[]).map((key) => (
						<label
							key={key}
							className="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
							data-testid={`decline-option-${key}`}
						>
							<input
								type="radio"
								name="decline-reason"
								value={key}
								checked={category === key}
								onChange={() => setCategory(key)}
								className="mt-0.5 text-blue-600"
							/>
							<span>{DECLINE_REASON_LABELS[key]}</span>
						</label>
					))}
				</div>
				{category === "other" && (
					<div>
						<label htmlFor="decline-other-reason" className="mb-1 block text-xs font-medium text-gray-500">
							Specify reason:
						</label>
						<textarea
							id="decline-other-reason"
							value={otherReason}
							onChange={(e) => setOtherReason(e.target.value)}
							placeholder="Enter the reason for declining..."
							rows={3}
							maxLength={500}
							className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-300"
							data-testid="decline-other-reason-input"
						/>
					</div>
				)}
			</div>
		</Modal>
	);
};
